import { useAuthStore } from 'shared/hooks/stores/useAuthStore';

const buildUrl = (parts: string[], queryParams?: Record<string, string | undefined>) => {
  const path = parts.filter(Boolean).join('/');

  if (!queryParams) {
    return path;
  }

  const validParams: Record<string, string> = Object.fromEntries(
    Object.entries(queryParams)
      .filter(([_, value]) => typeof value === 'string')
      .map(([key, value]) => [key, value as string]),
  );

  if (Object.keys(validParams).length === 0) {
    return path;
  }

  const queryString = new URLSearchParams(validParams).toString();
  return `${path}?${queryString}`;
};

const authSitemap = Object.freeze({
  main: '/',
  onboarding: (stepName = '', jobOrderId?: string) =>
    buildUrl(['/onboarding', stepName], { jobOrderId }),
  jobOrder: (jobOrderId: string, externalOfferId?: string) =>
    buildUrl(['/job-orders', jobOrderId, externalOfferId || '']),
  application: (jobOrderId: string) =>
    buildUrl(['/job-orders', jobOrderId], { application: 'true' }),
  offers: '/',
  observed: '/observed',
  applications: (tabName = '') => buildUrl(['/applications', tabName]),
  profile: (tabName = '') => buildUrl(['/profile', tabName]),
  settings: '/settings',
  notifications: '/notifications',
  externalOffer: (externalOfferId: string) => `/offer/${externalOfferId}`,
  externalOfferSuccess: (externalOfferId: string, jobOrderId?: string) =>
    `/offer/${externalOfferId}/success${jobOrderId ? `?jobOrderId=${jobOrderId}` : ''}`,
});

const defaultSitemap = Object.freeze({
  main: '/',
  onboarding: (_stepName?: string, _jobOrderId?: string) => '/',
  jobOrder: (_jobOrderId: string) => '/',
  application: (_jobOrderId: string) => '/',
  offers: '/',
  observed: '/',
  applications: (_tabName?: string) => '/',
  profile: (_tabName?: string) => '/',
  settings: '/',
  notifications: '/',
  externalOffer: (externalOfferId: string) => `/offer/${externalOfferId}`,
  externalOfferSuccess: (externalOfferId: string, jobOrderId?: string) =>
    `/offer/${externalOfferId}/success${jobOrderId ? `?jobOrderId=${jobOrderId}` : ''}`,
});

export const useSitemap = () => {
  const authenticated = useAuthStore((state) => state.authenticated);
  if (authenticated) {
    return authSitemap;
  }
  return defaultSitemap;
};
